/**
 * apiPatch — global production safety net so the deployed frontend ALWAYS
 * talks to its OWN backend, regardless of what URL was baked in at build time.
 *
 * Why this exists:
 *   `REACT_APP_BACKEND_URL` is inlined at build time. If the build was created
 *   in the preview environment, the string `https://ari-decision-hub.preview...`
 *   ends up in 167 spots in the production bundle. On `remoddash.com` those
 *   calls cross-origin to the preview backend, which:
 *     - Fails when preview is offline
 *     - Fails CORS (preview backend doesn't whitelist remoddash.com origin)
 *     - Worst case: succeeds, but reads/writes go to the WRONG database.
 *
 * What it does:
 *   1. Sets axios.defaults.baseURL to the current origin in production.
 *   2. Monkey-patches `window.fetch` to rewrite any preview-URL in the
 *      outgoing request URL → current origin.
 *   3. No-op for localhost dev (developer-friendly).
 *
 * Load order: imported FIRST in `index.js` so the patch is installed before
 * any page module evaluates its `API` constant.
 */
import axios from 'axios';

const PREVIEW_URL_PATTERN = /https:\/\/[^/]*\.preview\.emergentagent\.com/g;

function detectBackend() {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  // Dev/local — trust the env var or default to 8001
  if (host === 'localhost' || host === '127.0.0.1' || host === '') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  // Everywhere else (preview, prod, custom domains) — same origin.
  // The k8s ingress on both preview and prod proxies /api/* to backend.
  return window.location.origin;
}

const RUNTIME_BACKEND = detectBackend();

// ── axios global default ────────────────────────────────────────────
// Only set baseURL if the consumer is using a relative path. Most of our
// 167 spots build absolute URLs from REACT_APP_BACKEND_URL, so we handle
// those via the fetch + axios interceptor below. baseURL is a safety net
// for any future code that uses relative paths.
axios.defaults.baseURL = RUNTIME_BACKEND;

// ── axios request interceptor ───────────────────────────────────────
axios.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  if (typeof config.url === 'string') {
    config.url = config.url.replace(PREVIEW_URL_PATTERN, RUNTIME_BACKEND);
  }
  if (typeof config.baseURL === 'string') {
    config.baseURL = config.baseURL.replace(PREVIEW_URL_PATTERN, RUNTIME_BACKEND);
  }
  return config;
});

// ── fetch monkey-patch ──────────────────────────────────────────────
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const _origFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    let target = input;
    if (typeof target === 'string') {
      target = target.replace(PREVIEW_URL_PATTERN, RUNTIME_BACKEND);
    } else if (target instanceof Request) {
      const newUrl = target.url.replace(PREVIEW_URL_PATTERN, RUNTIME_BACKEND);
      if (newUrl !== target.url) {
        target = new Request(newUrl, target);
      }
    }
    return _origFetch(target, init);
  };
}

// One-time visible breadcrumb so we can confirm the patch loaded in a
// production tab via DevTools. Keep it quiet (debug level).
if (typeof console !== 'undefined' && console.debug) {
  console.debug(`[apiPatch] runtime backend = ${RUNTIME_BACKEND}`);
}

export { RUNTIME_BACKEND };
